import type { Dispatch, SetStateAction } from "react"

import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"

import { WorkflowInputFeature } from "../../../types/generated/api/WorkflowInputFeature"
import type { Schema } from "../Interfaces"

export function useSchemaForm(
  schema: Schema,
  setSchema: Dispatch<SetStateAction<Schema>>,
): {
  handleInputChange: (e: InputChangeEvent, key: string) => void
  handleNameChange: (e: InputChangeEvent, key: string) => void
  handleTypeChange: (e: InputChangeEvent, key: string) => void
  handleRemoveFeature: (key: string) => void
} {
  const typeCastBasedOnType = (value: unknown, type: WorkflowInputFeature.type): unknown => {
    switch (type) {
      case WorkflowInputFeature.type.NUMBER:
        return isNaN(Number(value)) ? 0 : Number(value)
      case WorkflowInputFeature.type.BOOLEAN:
        // Convert value to string, make it lowercase, and compare with "true"
        // This handles various boolean string representations ("True", "TRUE", "true")
        // Returns true only if the lowercase value exactly matches "true", false otherwise
        return String(value).toLowerCase() === "true"
      default:
        return value
    }
  }

  const handleInputChange = (e: InputChangeEvent, key: string): void => {
    setSchema((prev) => {
      if (prev[key]) {
        return {
          ...prev,
          [key]: {
            ...prev[key],
            value: e.target.value,
          },
        }
      }

      return prev
    })
  }

  const handleNameChange = (e: InputChangeEvent, key: string): void => {
    setSchema((prev) => {
      if (prev[key]) {
        return {
          ...prev,
          [key]: {
            ...prev[key],
            name: e.target.value as string,
          },
        }
      }

      return prev
    })
  }

  const handleTypeChange = (e: InputChangeEvent, key: string): void => {
    const newType = (e.target.value as string).toUpperCase() as WorkflowInputFeature.type

    setSchema((prev) => {
      if (prev[key]) {
        return {
          ...prev,
          [key]: {
            ...prev[key],
            type: newType,
            value: typeCastBasedOnType(prev[key].value, newType),
          },
        }
      }

      return prev
    })
  }

  const handleRemoveFeature = (key: string): void => {
    const newSchema = schema
    delete newSchema[key]

    setSchema({ ...newSchema })
  }

  return {
    handleInputChange,
    handleNameChange,
    handleTypeChange,
    handleRemoveFeature,
  }
}
